import { OGLRenderingContext, RenderTarget, Texture } from 'ogl';

import { DoubleFBO } from './HeroGradient.types';

/**
 * Load an external image and return a ImageBitmap instance
 *
 * @param src The image source URL
 * @returns A ImageBitmap instance
 */
export function loadImageBitmap(src: string): Promise<ImageBitmap> {
  return fetch(src)
    .then((res) => res.blob())
    .then((blob) => createImageBitmap(blob));
}

/**
 * Load an external image and apply it to a texture
 *
 * @param tex Texture instance
 * @param src Image source URL
 */
export function loadTextureImage(tex: Texture, src: string) {
  return loadImageBitmap(src).then((bitmap) => {
    /** @ts-expect-error Passing in a DataView or UInt8Array here does not work even though they qualify as a valid ArrayBufferView. Passing in an ImageBitmap is not recognized as a valid `ImageRepresentation` however it does work. */
    tex.image = bitmap;
  });
}

/**
 * Check if a texture format is supported
 */
function supportRenderTextureFormat(
  gl: WebGL2RenderingContext,
  internalFormat: number,
  format: number,
  type: number,
) {
  const texture = gl.createTexture();

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null);

  const fbo = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(
    gl.FRAMEBUFFER,
    gl.COLOR_ATTACHMENT0,
    gl.TEXTURE_2D,
    texture,
    0,
  );

  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  if (status != gl.FRAMEBUFFER_COMPLETE) return false;
  return true;
}

export function getSupportedFormat(
  gl: WebGL2RenderingContext,
  internalFormat: number,
  format: number,
  type: number,
) {
  if (!supportRenderTextureFormat(gl, internalFormat, format, type)) {
    switch (internalFormat) {
      case gl.R16F:
        return getSupportedFormat(gl, gl.RG16F, gl.RG, type);
      case gl.RG16F:
        return getSupportedFormat(gl, gl.RGBA16F, gl.RGBA, type);
      default:
        return null;
    }
  }
  return { internalFormat, format };
}

/**
 * Helper to create a ping-pong FBO pairing for simulating on GPU
 *
 * @param gl OGLRenderingContext instance
 * @param options RenderTarget options
 * @returns Double FBO object
 */
export function createDoubleFBO(
  gl: OGLRenderingContext,
  options: {
    width?: number;
    height?: number;
    wrapS?: number;
    wrapT?: number;
    minFilter?: number;
    maxFilter?: number;
    type?: number;
    format?: number;
    internalFormat?: number;
    depth?: boolean;
  } = {
    width: gl.canvas.width,
    height: gl.canvas.height,
    minFilter: gl.LINEAR,
    maxFilter: gl.LINEAR,
  },
): DoubleFBO {
  const fbo = {
    read: new RenderTarget(gl, options),
    write: new RenderTarget(gl, options),
    swap: () => {
      const temp = fbo.read;
      fbo.read = fbo.write;
      fbo.write = temp;
    },
  };
  return fbo;
}
